import { setFeaturedDiscoverItems } from 'actions/requests';
import Button from 'components/atoms/Button';
import Typography from 'components/atoms/Typography';
import MultiCarousel from 'components/molecules/MultiCarousel';
import Select from 'components/molecules/Select';
import { FeatureableDiscoverItem, FeatureableDiscoverItemDescriptor } from 'utils/gloo-types';
import { isEqual } from 'lodash';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { cloneArrayWithSwappedElements } from 'utils/common';
import {
  discoverItemForFeatureableDiscoverItem,
  selectOptionForFeatureableDiscoverItem,
  featureableDiscoverItemOrderDescriptorsFromFeatureableDiscoverItems,
  sortSelectOptionByLabel,
  useDiscoverItemAuthorSelect,
} from 'utils/discover';
import { SelectOption } from 'utils/frontendTypes';
import { useThunkDispatch } from 'utils/reduxHook';
import DiscoverCardPreview from 'pages/_AdminAuthoringPages/_components/DiscoverCardPreview';

interface AdminAuthoringFeaturedSectionProps {
  featuredItems: FeatureableDiscoverItem[];
  setFeaturedItems: Dispatch<SetStateAction<FeatureableDiscoverItem[]>>;
  featureableItems: FeatureableDiscoverItem[];
}

const responsive = {
  xxxl: {
    breakpoint: { max: 9999, min: 1250 },
    items: 5.5,
    partialVisibilityGutter: 0,
  },
  xxl: {
    breakpoint: { max: 1250, min: 1150 },
    items: 3.2,
    partialVisibilityGutter: 60,
  },
  xl: {
    breakpoint: { max: 1150, min: 1024 },
    items: 2,
    partialVisibilityGutter: 60,
  },
  lg: {
    breakpoint: { max: 1024, min: 768 },
    items: 1.5,
    partialVisibilityGutter: 60,
  },
  sm: {
    breakpoint: { max: 768, min: 0 },
    items: 1.25,
    partialVisibilityGutter: 60,
  },
};

const AdminAuthoringFeaturedSection = ({
  featuredItems,
  setFeaturedItems,
  featureableItems,
}: AdminAuthoringFeaturedSectionProps) => {
  const dispatch = useThunkDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const generateFeatureableSelectOptions = (): SelectOption<FeatureableDiscoverItemDescriptor>[] => {
    return featureableItems
      .map(selectOptionForFeatureableDiscoverItem)
      .sort(sortSelectOptionByLabel)
      .filter(
        (featureableItem) =>
          !featuredItems.some(
            (featuredItem) =>
              featureableItem.value.type === featuredItem.type && featureableItem.value.id === featuredItem.item.id,
          ),
      );
  };

  const [featureableSelectOptions, setFeatureableSelectOptions] = useState(generateFeatureableSelectOptions());

  const currentDescriptors = useMemo(
    () => featureableDiscoverItemOrderDescriptorsFromFeatureableDiscoverItems(featuredItems),
    [featuredItems],
  );

  const updateFeaturedItems = async (
    descriptors: FeatureableDiscoverItemDescriptor[],
    afterSuccess?: () => void,
  ): Promise<void> => {
    setIsLoading(true);
    try {
      await dispatch(setFeaturedDiscoverItems({ order: descriptors })).then(setFeaturedItems);
      if (afterSuccess) {
        afterSuccess();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSelectFeatureableDiscoverItem = async (descriptor: SelectOption<FeatureableDiscoverItemDescriptor>) => {
    const updatedDescriptors = [...currentDescriptors, descriptor.value];
    // Remove descriptor from the select options
    const afterSuccess = () => {
      setFeatureableSelectOptions(
        featureableSelectOptions.filter((selectItem) => !isEqual(selectItem.value, descriptor.value)),
      );
    };
    updateFeaturedItems(updatedDescriptors, afterSuccess);
  };

  const handleCardClick = useDiscoverItemAuthorSelect();

  const cards = featuredItems.map((hubResource, i) => {
    const item = discoverItemForFeatureableDiscoverItem(hubResource);

    const onMoveLeft = () => {
      const updatedDescriptors = cloneArrayWithSwappedElements(currentDescriptors, i, i - 1);
      updateFeaturedItems(updatedDescriptors);
    };

    const onMoveRight = () => {
      const updatedDescriptors = cloneArrayWithSwappedElements(currentDescriptors, i, i + 1);
      updateFeaturedItems(updatedDescriptors);
    };

    const onUnfeature = () => {
      const updatedDescriptors = [...currentDescriptors];
      const [removedDescriptor] = updatedDescriptors.splice(i, 1);
      const itemToRemove = featuredItems.find(
        (featuredItem) => featuredItem.type === removedDescriptor.type && featuredItem.item.id === removedDescriptor.id,
      );
      // Add descriptor back into search bar so it can be re-inserted without a page load
      const afterSuccess = () => {
        const selectItemForUnfeatured = selectOptionForFeatureableDiscoverItem(itemToRemove);
        setFeatureableSelectOptions(
          [...featureableSelectOptions, selectItemForUnfeatured].sort(sortSelectOptionByLabel),
        );
      };

      updateFeaturedItems(updatedDescriptors, afterSuccess);
    };

    return (
      <div key={i} className="w-full px-2 pb-8 h-full">
        <DiscoverCardPreview key={i} itemCard={item} className="w-full h-full" onClick={handleCardClick} />
        <div className="flex flex-row absolute top-1 right-5">
          <Button
            onClick={onMoveLeft}
            icon="arrow-left"
            dataDurableId="hubResourceDemoteButton"
            size="xs"
            type="white"
            buttonType="button"
            hideHighlight
            disabled={isLoading || i === 0}
          />
          <Button
            onClick={onMoveRight}
            icon="arrow-right"
            dataDurableId="hubResourcePromoteButton"
            size="xs"
            type="white"
            buttonType="button"
            hideHighlight
            className="mx-1"
            disabled={isLoading || i === featuredItems.length - 1}
          />
          <Button
            onClick={onUnfeature}
            icon="trash"
            dataDurableId="hubResourceRemoveButton"
            size="xs"
            type="destruction"
            hideHighlight
            buttonType="button"
            disabled={isLoading}
          />
        </div>
      </div>
    );
  });

  return (
    <div>
      <Typography className="mt-8 mb-4 text-center md:text-left text-blackDark" variant="h3">
        Featured
      </Typography>

      <div className="flex flex-col gap-4">
        <Select
          dataDurableId="adminAuthoring"
          label="Add Resources to Featured"
          options={featureableSelectOptions}
          onChange={onSelectFeatureableDiscoverItem}
          disabled={isLoading}
        />
        <div className="w-full">
          <MultiCarousel items={cards} responsiveObject={responsive} width="100vw - 5.5rem" />
        </div>
      </div>
    </div>
  );
};

export default AdminAuthoringFeaturedSection;
